import { Chip } from "@mui/material";
import { safeBoolean } from "../../utils/CommonFunctions";
import {
	MyBusinessIcon,
	MyCancelIcon,
	MyCheckCircleIcon,
	MyCheckIcon,
	MyCreditCardIcon,
	MyEmailIcon,
	MyLanguageIcon,
	MyPersonIcon,
	MyPhoneIcon,
	MyVerifiedIcon,
	MyCalendarIcon,
	MyLocationIcon,
	MyMapIcon,
	MyAccountBalanceWalletIcon,
	MyAttachMoneyIcon,
	MyVisibilityIcon,
	MyVisibilityOffIcon,
} from "../../Assets/svg/MyIcons";
import "./TenantCreationPreview.css";

const displayValue = (value, fallback = "-") => {
	if (value === null || value === undefined) return fallback;
	if (typeof value === "string") {
		const trimmed = value.trim();
		if (!trimmed || trimmed.toLowerCase() === "null" || trimmed.toLowerCase() === "undefined") {
			return fallback;
		}
		return trimmed;
	}
	if (Number.isNaN(value)) return fallback;
	return value;
};

const formatMoney = (amount, symbol = "$") => {
	if (amount === null || amount === undefined || amount === "" || Number.isNaN(Number(amount))) {
		return "-";
	}
	return `${symbol} ${Number(amount).toFixed(2)}`;
};

const InfoRow = ({ label, value, icon, span = 6 }) => (
	<div className={`tp-info tp-span-${span}`}>
		<p className="tp-info-label">{label}</p>
		<div className="tp-info-value">
			{icon}
			<strong>{displayValue(value)}</strong>
		</div>
	</div>
);

const StatusField = ({ label, children, span = 4 }) => (
	<div className={`tp-status tp-span-${span}`}>
		<p className="tp-status-label">{label}</p>
		{children}
	</div>
);

const PreviewPanel = ({ title, icon, children }) => (
	<section className="tp-panel">
		<div className="tp-panel-head">
			<span className="tp-panel-icon" aria-hidden>
				{icon}
			</span>
			<h3>{title}</h3>
		</div>
		<div className="tp-panel-body">{children}</div>
	</section>
);

const PreviewSection = ({ title, children }) => (
	<div className="tp-section">
		<h4 className="tp-section-title">{title}</h4>
		{children}
	</div>
);

const PlanPanel = ({ title, icon, children }) => (
	<aside className="tp-plan">
		<div className="tp-plan-head">
			<span className="tp-plan-head-icon" aria-hidden>
				{icon}
			</span>
			<h3>{title}</h3>
		</div>
		<div className="tp-plan-body">{children}</div>
	</aside>
);

const PlanBlock = ({ title, children }) => (
	<div className="tp-plan-block">
		<h4 className="tp-plan-block-title">{title}</h4>
		{children}
	</div>
);

const PlanRow = ({ label, icon, value, isPrice = false }) => (
	<div>
		<p className="tp-plan-row-label">{label}</p>
		<div className={`tp-plan-row-value${isPrice ? " is-price" : ""}`}>
			{icon}
			<span>{displayValue(value)}</span>
		</div>
	</div>
);

const TenantCreationPreview = ({
	previewData,
	planMapPrice,
	selectedCurrency,
	tenantCurrencies,
	merchantDetails,
	isPlanChanged = false,
	kycCountryList = [],
	kycPlanList = [],
	tripolaPlanList,
	type
}) => {
	// console.log("Preview data", previewData)
	// console.log("TYPE RECEIVED:", type);
	const selectedPlan = planMapPrice?.[previewData?.qbotPlan?.planId] || {};
	const currentPlanDetails = selectedPlan;
	const currentAmount =
		previewData?.qbotPlan?.billingCycle === "monthly"
			? selectedPlan?.monthly
			: selectedPlan.yearly;
	const currencyDetails = tenantCurrencies?.find(
		(c) => c.id === Number(selectedCurrency),
	) || { exchangeRate: "1.2000", symbol: "$" };

	const selectedKycPlan = kycPlanList.find(
		(plan) => String(plan.id) === String(previewData?.kyc?.kycPlanId)
	) || {};

	const selectedTripolaPlan =
		tripolaPlanList?.find((plan) => String(plan.id) === String(previewData?.tripolaPlan?.tripolaPlanId)) || {};

	const kycBillingCycle = previewData?.kyc?.kycBillingCycle;
	const kycPlanPrice =
		String(kycBillingCycle).toLowerCase() === "yearly"
			? selectedKycPlan?.yearly_base_price
			: selectedKycPlan?.monthly_base_price;
	const kycIncludedVerifications =
		String(kycBillingCycle).toLowerCase() === "yearly"
			? selectedKycPlan?.yearly_included_verification
			: selectedKycPlan?.monthly_included_verification;

	const tripolaBillingCycle = previewData?.tripolaPlan?.tripolaBillingCycle;
	const tripolaPlanPrice =
		String(tripolaBillingCycle).toLowerCase() === "yearly"
			? selectedTripolaPlan?.yearly_price
			: selectedTripolaPlan?.monthly_price;

	let noteText = "";
	if (
		previewData?.qbotPlan?.planHistory &&
		previewData?.qbotPlan.planHistory[0].paymentMode !== previewData?.qbotPlan.paymentMode &&
		previewData?.qbotPlan.paymentMode === "online"
	) {
		noteText =
			"The payment mode will be changed to Online. A payment link will be sent to the tenant’s email two days before the current plan expires.";
	} else if (
		previewData?.qbotPlan?.planHistory &&
		previewData?.qbotPlan.planHistory[0].paymentMode !== previewData?.qbotPlan.paymentMode &&
		previewData?.qbotPlan.paymentMode === "offline"
	) {
		noteText =
			"The payment mode will be changed to Offline. The admin must manually verify the payment after it is received from the tenant.";
	} else if (
		previewData?.qbotPlan?.planHistory &&
		previewData?.qbotPlan.planHistory[0].paymentMode === previewData?.qbotPlan.paymentMode &&
		previewData?.qbotPlan.isAutoPay
	) {
		if (
			previewData?.qbotPlan.planHistory &&
			previewData?.qbotPlan.planHistory[0].paymentMode === "online"
		) {
			noteText =
				"Subscription is enabled for this tenant. The payment will be automatically deducted on the due date.";
		} else {
			noteText =
				"Subscription is enabled for this tenant. The admin must manually verify the payment once it is received from the tenant on the due date.";
		}
	} else {
		noteText =
			"Subscription is disabled for this tenant. The admin must manually process the payment on the due date.";
	}

	const getFeatureValue = (item) => {
		const featureName = (item?.feature || "").toLowerCase();
		let value;

		// For custom plans, check custom limits and handle null as "Unlimited"
		if (previewData?.qbotPlan?.isCustomPlan && previewData?.qbotPlan?.customPlanDetails) {
			if (
				featureName.includes("user") &&
				previewData?.qbotPlan.customPlanDetails.userLimit !== undefined
			) {
				value =
					previewData?.qbotPlan.customPlanDetails.userLimit === null
						? "Unlimited"
						: previewData?.qbotPlan.customPlanDetails.userLimit;
			} else if (
				featureName.includes("message") &&
				previewData?.qbotPlan.customPlanDetails.messageLimit !== undefined
			) {
				value =
					previewData?.qbotPlan.customPlanDetails.messageLimit === null
						? "Unlimited"
						: previewData?.qbotPlan.customPlanDetails.messageLimit;
			} else if (
				featureName.includes("agent") &&
				previewData?.qbotPlan.customPlanDetails.agentLimit !== undefined
			) {
				value =
					previewData?.qbotPlan.customPlanDetails.agentLimit === null
						? "Unlimited"
						: previewData?.qbotPlan.customPlanDetails.agentLimit;
			} else {
				value =
					item?.value === null ? "Unlimited" : (item?.value ?? "");
			}
		} else {
			// Return the plan's default feature value (handle null as "Unlimited" too)
			value = item?.value === null ? "Unlimited" : (item?.value ?? "");
		}

		// Capitalize "unlimited" if it comes from backend as lowercase
		if (typeof value === "string" && value.toLowerCase() === "unlimited") {
			return "Unlimited";
		}
		return value;
	};

	// const isQbot = previewData?.common?.isQbot === true || previewData?.common?.isQbot === 1 || type === "qbot";
	// const isPaycentral = previewData?.common?.isPaycentral === true || previewData?.common?.isPaycentral === 1 || type === "paycentral";
	// const isKyc = previewData?.common?.isKyc === true || previewData?.common?.isKyc === 1 || type === "kyc";
	// const isTripola = previewData?.common?.isTripola === true || previewData?.common?.isTripola === 1 || type === "tripola";

	const isQbot = type === "qbot";
	const isPaycentral = type === "paycentral";
	const isKyc = type === "kyc";
	const isTripola = type === "tripola";

	const selectedKycCountry = kycCountryList.find(
		(country) => country.code === previewData?.kyc?.kycCountry
	);

	return (
		<div className="tp-preview">
			{/* Tenant Information */}
			<PreviewPanel title="Tenant Information" icon={<MyPersonIcon />}>
				<PreviewSection title="Personal Details">
					<div className="tp-grid">
						{[
							{
								label: "First Name",
								value: previewData?.common.firstName,
								icon: <MyPersonIcon fontSize="small" />,
								span: 6,
							},
							{
								label: "Last Name",
								value: previewData?.common.lastName,
								icon: <MyPersonIcon fontSize="small" />,
								span: 6,
							},
							{
								label: "Email",
								value: previewData?.common.email,
								icon: <MyEmailIcon fontSize="small" />,
								span: 12,
							},
						].map((item, index) => (
							<InfoRow
								key={index}
								label={item.label}
								value={item.value}
								icon={item.icon}
								span={item.span}
							/>
						))}
					</div>
				</PreviewSection>
			</PreviewPanel>

			{/* Qbot */}
			{isQbot && (
				<div className="tp-row">
					<div className="tp-stack">
						<PreviewPanel title="Qbot Company Details" icon={<MyBusinessIcon />}>
							<PreviewSection title="Company Information">
								<div className="tp-grid">
									{[
										{
											label: "Company Name",
											value: previewData?.qbot?.companyName,
											icon: <MyBusinessIcon fontSize="small" />,
											span: 6,
										},
										{
											label: "Company Email",
											value: previewData?.qbot?.companyEmail,
											icon: <MyEmailIcon fontSize="small" />,
											span: 6,
										},
										{
											label: "Domain URL",
											value: previewData?.qbot?.domainUrl,
											icon: <MyLanguageIcon fontSize="small" />,
											span: 6,
										},
										{
											label: "Phone Number",
											value: previewData?.qbot?.companyPhoneNumber,
											icon: <MyPhoneIcon fontSize="small" />,
											span: 6,
										},
										{
											label: "Address",
											value: previewData?.common.companyAddress,
											icon: <MyBusinessIcon fontSize="small" />,
											span: 12,
										},
										{
											label: "Zip Code",
											value: previewData?.qbot?.companyZipCode,
											icon: <MyLocationIcon fontSize="small" />,
											span: 4,
										},
										{
											label: "Country",
											value: previewData?.common.companyCountry,
											icon: <MyLanguageIcon fontSize="small" />,
											span: 4,
										},
										{
											label: "State",
											value: previewData?.common.companyState,
											icon: <MyMapIcon fontSize="small" />,
											span: 4,
										},
									].map((item, index) => (
										<InfoRow
											key={index}
											label={item.label}
											value={item.value}
											icon={item.icon}
											span={item.span}
										/>
									))}
								</div>
							</PreviewSection>
						</PreviewPanel>

						<PreviewPanel title="Tenant Configuration" icon={<MyVerifiedIcon />}>
							<PreviewSection title="Status">
								<div className="tp-grid">
									<StatusField label="Domain Verification" span={4}>
										<Chip
											className="tp-chip"
											icon={
												previewData?.qbot?.isVerified ? (
													<MyVerifiedIcon />
												) : (
													<MyCancelIcon />
												)
											}
											label={
												previewData?.qbot?.isVerified
													? "Verified"
													: "Unverified"
											}
											color={
												previewData?.qbot?.isVerified
													? "success"
													: "error"
											}
											size="medium"
										/>
									</StatusField>
									<StatusField label="Subscription Status" span={4}>
										<Chip
											className={`tp-chip ${safeBoolean(previewData?.qbotPlan?.isAutoPay)
												? "text-success"
												: "text-danger"
												}`}
											icon={
												safeBoolean(
													previewData?.qbotPlan?.isAutoPay,
												) ? (
													<MyCheckCircleIcon className="text-success" />
												) : (
													<MyCancelIcon className="text-danger" />
												)
											}
											label={
												safeBoolean(previewData?.qbotPlan?.isAutoPay)
													? "Enabled"
													: "Disabled"
											}
											sx={{ fontWeight: 600 }}
										/>
									</StatusField>
									<StatusField label="Currency" span={4}>
										<Chip
											className="tp-chip is-navy"
											icon={
												<span
													style={{
														fontSize: "1.1rem",
														fontWeight: 600,
														lineHeight: 1,
														marginLeft: "10px",
													}}
												>
													{currencyDetails?.symbol}
												</span>
											}
											label={
												currencyDetails?.name ||
												currencyDetails?.currencyName ||
												""
											}
											size="medium"
											sx={{
												"& .MuiChip-label": {
													px: 2,
												},
											}}
										/>
									</StatusField>

									{previewData?.qbotPlan?.paymentMode === "offline" && (
										<StatusField label="Billing Details Visibility" span={12}>
											<Chip
												className="tp-chip"
												icon={
													previewData?.qbotPlan?.hideBillingDetails ? (
														<MyVisibilityOffIcon />
													) : (
														<MyVisibilityIcon />
													)
												}
												label={
													previewData?.qbotPlan?.hideBillingDetails
														? "Hidden - Tenant cannot view billing details"
														: "Visible - Tenant can view billing details"
												}
												size="medium"
												sx={{
													fontWeight: 600,
													minWidth: "300px",
													justifyContent: "flex-start",
												}}
											/>
										</StatusField>
									)}
								</div>
							</PreviewSection>
						</PreviewPanel>

						<PreviewPanel title="Allowed Features" icon={<MyCheckCircleIcon />}>
							<PreviewSection title="Features">
								<div className="tp-grid">
									{[
										{ label: "User Chat", key: "user_chat" },
										{ label: "AI Chat", key: "ai_chat" },
										{
											label: "Support Ticket",
											key: "support_tickets",
										},
										{
											label: "Web Mail Tickets",
											key: "web_mail_tickets",
										},
									].map((feature) => {
										const isEnabled = safeBoolean(
											previewData?.qbotPlan?.allowedFeatures?.[
											feature.key
											],
										);
										return (
											<div className="tp-feature tp-span-3" key={feature.key}>
												<p className="tp-info-label">{feature.label}</p>
												<span
													className={`tp-feature-state ${isEnabled ? "is-on" : "is-off"
														}`}
												>
													{isEnabled ? (
														<MyCheckCircleIcon fontSize="small" className="text-success" />
													) : (
														<MyCancelIcon fontSize="small" className="text-danger" />
													)}
													{isEnabled ? "Enabled" : "Disabled"}
												</span>
											</div>
										);
									})}
								</div>
							</PreviewSection>
						</PreviewPanel>
					</div>

					<PlanPanel title="Qbot Plan Information" icon={<MyCreditCardIcon />}>
						<PlanBlock title={`Plan Details${isPlanChanged ? " (upcoming)" : ""}`}>
							<div className="tp-plan-rows">
								{[
									{
										label: "Plan Name",
										value: previewData?.qbotPlan?.planId
											? `${displayValue(selectedPlan?.name, "Plan")}${
													previewData?.qbotPlan?.isCustomPlan
														? " (Custom)"
														: ""
												}`
											: "-",
										icon: <MyCreditCardIcon fontSize="small" />,
										isPrice: false,
									},
									{
										label: "Plan Price",
										value: previewData?.qbotPlan?.planId
											? previewData?.qbotPlan?.isCustomPlan &&
												previewData?.qbotPlan.customPlanDetails
													?.price !== null &&
												previewData?.qbotPlan.customPlanDetails
													?.price !== undefined
												? formatMoney(
														previewData.qbotPlan.customPlanDetails.price,
														currencyDetails?.symbol
													)
												: currentAmount
													? formatMoney(
															Number(currentAmount) *
																Number(currencyDetails?.exchangeRate),
															currencyDetails?.symbol
														)
													: formatMoney(0, currencyDetails?.symbol)
											: "-",
										icon: <MyAccountBalanceWalletIcon fontSize="small" />,
										isPrice: true,
									},
								].map((item, index) => (
									<PlanRow
										key={index}
										label={item.label}
										icon={item.icon}
										value={item.value}
										isPrice={item.isPrice}
									/>
								))}
							</div>
						</PlanBlock>

						<PlanBlock
							title={`Billing Information${isPlanChanged ? " (upcoming)" : ""}`}
						>
							<div className="tp-plan-rows">
								{[
									{
										label: "Payment Mode",
										value: previewData?.qbotPlan?.paymentMode || "-",
										icon: <MyCreditCardIcon fontSize="small" />,
									},
									{
										label: "Billing Cycle",
										value: previewData?.qbotPlan?.billingCycle || "-",
										icon: <MyCalendarIcon fontSize="small" />,
									},
								].map((item, index) => (
									<PlanRow
										key={index}
										label={item.label}
										icon={item.icon}
										value={item.value}
									/>
								))}
							</div>
						</PlanBlock>

						{currentPlanDetails?.features && (
							<PlanBlock
								title={`Plan Features${isPlanChanged ? " (upcoming)" : ""}`}
							>
								<div className="tp-plan-rows">
									{currentPlanDetails?.features?.map(
										(item, index) => {
											const value = getFeatureValue(item);

											const featureName = (
												item?.feature || ""
											).toLowerCase();
											// Determine which icon to use based on feature name
											let FeatureIcon = MyCheckIcon;
											if (featureName.includes("user")) {
												FeatureIcon = MyPersonIcon;
											} else if (
												featureName.includes("message")
											) {
												FeatureIcon = MyEmailIcon;
											} else if (
												featureName.includes("agent")
											) {
												FeatureIcon = MyCheckCircleIcon;
											}

											return (
												<PlanRow
													key={index}
													label={item.feature}
													icon={<FeatureIcon fontSize="small" />}
													value={displayValue(value)}
												/>
											);
										},
									)}
								</div>
							</PlanBlock>
						)}

						{previewData?.qbot?.id && (
							<div className="tp-note">
								<strong>Note</strong>
								<p>{noteText}</p>
							</div>
						)}
					</PlanPanel>
				</div>
			)}

			{/* PayCentral */}
			{isPaycentral && (
				<div className="tp-row">
					<PreviewPanel title="Paycentral Corporation Information" icon={<MyBusinessIcon />}>
						<PreviewSection title="Corporation Details">
							<div className="tp-grid">
								{[
									{
										label: "User Name",
										value: previewData?.paycentral?.userName,
										icon: <MyPersonIcon fontSize="small" />,
										span: 6,
									},
									{
										label: "Corporation Name",
										value: previewData?.paycentral?.organizationName,
										icon: <MyBusinessIcon fontSize="small" />,
										span: 6,
									},
									{
										label: "Domain",
										value: previewData?.paycentral?.domainUrl,
										icon: <MyLanguageIcon fontSize="small" />,
										span: 6,
									},
									{
										label: "Business Name",
										value: previewData?.paycentral?.businessName,
										icon: <MyBusinessIcon fontSize="small" />,
										span: 6,
									},
									{
										label: "Business Type",
										value: previewData?.paycentral?.businessType,
										icon: <MyBusinessIcon fontSize="small" />,
										span: 6,
									},
									{
										label: "Business Country",
										value: previewData?.paycentral?.businessCountry,
										icon: <MyLanguageIcon fontSize="small" />,
										span: 6,
									},
									{
										label: "Business State",
										value: previewData?.paycentral?.businessState,
										icon: <MyMapIcon fontSize="small" />,
										span: 6,
									},
									{
										label: "Website",
										value: previewData?.paycentral?.website,
										icon: <MyLanguageIcon fontSize="small" />,
										span: 6,
									},
									{
										label: "Tax ID",
										value: previewData?.paycentral?.taxId,
										icon: <MyBusinessIcon fontSize="small" />,
										span: 6,
									},
								].map((item, index) => (
									<InfoRow
										key={index}
										label={item.label}
										value={item.value}
										icon={item.icon}
										span={item.span}
									/>
								))}

								<StatusField label="Active" span={6}>
									<Chip
										className="tp-chip"
										label={previewData?.paycentral?.isActive ? "Active" : "Inactive"}
										color={previewData?.paycentral?.isActive ? "success" : "default"}
									/>
								</StatusField>

								<StatusField label="Auto Invoice" span={6}>
									<Chip
										className="tp-chip"
										label={previewData?.paycentral?.isAutoInvoice ? "Enabled" : "Disabled"}
										color={previewData?.paycentral?.isAutoInvoice ? "success" : "default"}
									/>
								</StatusField>
							</div>
						</PreviewSection>
					</PreviewPanel>

					<PlanPanel title="Cost Information" icon={<MyCreditCardIcon />}>
						<PlanBlock title="Subscription Fees">
							<div className="tp-plan-rows">
								{[
									{
										label: "One-Time Platform Setup Fees",
										value: previewData?.paycentralCost?.oneTimeSetupFee,
									},
									{
										label: "Ongoing Maintenance & Support",
										value: previewData?.paycentralCost?.ongoingMaintenanceFee,
									},
									{
										label: "New Merchant Setup Fee",
										value: previewData?.paycentralCost?.newMerchantSetupFee,
									},
									{
										label: "Ongoing Maintenance per Merchant",
										value: previewData?.paycentralCost?.ongoingMaintenancePerMerchantFee,
									},
								].map((item, index) => (
									<PlanRow
										key={index}
										label={item.label}
										icon={<MyAttachMoneyIcon fontSize="small" />}
										value={formatMoney(
											item.value,
											currencyDetails?.symbol
										)}
									/>
								))}
							</div>
						</PlanBlock>
					</PlanPanel>
				</div>
			)}

			{/* KYC */}
			{isKyc && (
				<div className="tp-row">
					<PreviewPanel title="KYC Details" icon={<MyVerifiedIcon />}>
						<PreviewSection title="KYC Information">
							<div className="tp-grid">
								<InfoRow
									label="User Name"
									value={previewData?.kyc?.kycUserName}
									icon={<MyPersonIcon fontSize="small" />}
									span={6}
								/>
								<InfoRow
									label="Country"
									value={selectedKycCountry?.name || previewData?.kyc?.kycCountry}
									icon={<MyLanguageIcon fontSize="small" />}
									span={6}
								/>
								<InfoRow
									label="Company Name"
									value={previewData?.kyc?.kycCompanyName}
									icon={<MyBusinessIcon fontSize="small" />}
									span={6}
								/>
								<InfoRow
									label="Company Email"
									value={previewData?.kyc?.kycCompanyEmail}
									icon={<MyEmailIcon fontSize="small" />}
									span={6}
								/>
								<InfoRow
									label="Phone Number"
									value={previewData?.common?.companyPhoneNumber}
									icon={<MyPhoneIcon fontSize="small" />}
									span={6}
								/>
								<InfoRow
									label="Domain URL"
									value={previewData?.kyc?.kycDomainUrl}
									icon={<MyLanguageIcon fontSize="small" />}
									span={6}
								/>
								<InfoRow
									label="Auto Pay"
									value={previewData?.kyc?.kycIsAutoPay ? "Enabled" : "Disabled"}
									icon={<MyCheckCircleIcon fontSize="small" />}
									span={6}
								/>
							</div>
						</PreviewSection>
					</PreviewPanel>

					<PlanPanel title="KYC Plan Information" icon={<MyCreditCardIcon />}>
						<PlanBlock title={`Plan Details${isPlanChanged ? " (upcoming)" : ""}`}>
							<div className="tp-plan-rows">
								<PlanRow
									label="Plan Name"
									icon={<MyCreditCardIcon fontSize="small" />}
									value={
										previewData?.kyc?.kycPlanId
											? displayValue(selectedKycPlan?.name)
											: "-"
									}
								/>
								<PlanRow
									label="Plan Price"
									icon={<MyAccountBalanceWalletIcon fontSize="small" />}
									value={
										previewData?.kyc?.kycPlanId
											? formatMoney(kycPlanPrice, currencyDetails?.symbol)
											: "-"
									}
									isPrice
								/>
							</div>
						</PlanBlock>

						<PlanBlock
							title={`Billing Information${isPlanChanged ? " (upcoming)" : ""}`}
						>
							<div className="tp-plan-rows">
								<PlanRow
									label="Billing Cycle"
									icon={<MyCalendarIcon fontSize="small" />}
									value={displayValue(kycBillingCycle)}
								/>
								<PlanRow
									label="Included Verifications"
									icon={<MyCheckCircleIcon fontSize="small" />}
									value={
										previewData?.kyc?.kycPlanId
											? displayValue(kycIncludedVerifications)
											: "-"
									}
								/>
								<PlanRow
									label="Per Verification Price"
									icon={<MyAttachMoneyIcon fontSize="small" />}
									value={
										previewData?.kyc?.kycPlanId
											? formatMoney(
													selectedKycPlan?.per_verification_price,
													currencyDetails?.symbol
												)
											: "-"
									}
								/>
							</div>
						</PlanBlock>
					</PlanPanel>
				</div>
			)}

			{/* Tripola */}
			{isTripola && (
				<div className="tp-row">
					<PreviewPanel title="Tripola Company Details" icon={<MyBusinessIcon />}>
						<PreviewSection title="Company Information">
							<div className="tp-grid">
								{[
									{
										label: "Company Name",
										value: previewData?.tripola?.tripolaCompanyName,
										icon: <MyBusinessIcon fontSize="small" />,
										span: 6,
									},
									{
										label: "Company Email",
										value: previewData?.tripola?.tripolaCompanyEmail,
										icon: <MyEmailIcon fontSize="small" />,
										span: 6,
									},
									{
										label: "Domain URL",
										value: previewData?.tripola?.tripolaDomainUrl,
										icon: <MyLanguageIcon fontSize="small" />,
										span: 6,
									},
									{
										label: "Phone Number",
										value: previewData?.tripola?.tripolaCompanyPhoneNumber,
										icon: <MyPhoneIcon fontSize="small" />,
										span: 6,
									},
									{
										label: "Address",
										value: previewData?.common.companyAddress,
										icon: <MyBusinessIcon fontSize="small" />,
										span: 12,
									},
									{
										label: "City",
										value: previewData?.tripola?.tripolaCompanyCity,
										icon: <MyBusinessIcon fontSize="small" />,
										span: 12,
									},
									{
										label: "Zip Code",
										value: previewData?.tripola?.tripolaCompanyZipCode,
										icon: <MyLocationIcon fontSize="small" />,
										span: 4,
									},
									{
										label: "Country",
										value: previewData?.common.companyCountry,
										icon: <MyLanguageIcon fontSize="small" />,
										span: 4,
									},
									{
										label: "State",
										value: previewData?.common.companyState,
										icon: <MyMapIcon fontSize="small" />,
										span: 4,
									},
								].map((item, index) => (
									<InfoRow
										key={index}
										label={item.label}
										value={item.value}
										icon={item.icon}
										span={item.span}
									/>
								))}

								<StatusField label="Domain Verification" span={4}>
									<Chip
										className="tp-chip"
										icon={
											previewData?.tripola?.tripolaIsVerified ? (
												<MyVerifiedIcon />
											) : (
												<MyCancelIcon />
											)
										}
										label={
											previewData?.tripola?.tripolaIsVerified
												? "Verified"
												: "Unverified"
										}
										color={
											previewData?.tripola?.tripolaIsVerified
												? "success"
												: "error"
										}
										size="medium"
									/>
								</StatusField>
								<StatusField label="Auto Invoice" span={4}>
									<Chip
										className={`tp-chip ${safeBoolean(previewData?.tripolaPlan?.tripolaIsAutoInvoice)
											? "text-success"
											: "text-danger"
											}`}
										icon={
											safeBoolean(
												previewData?.tripolaPlan?.tripolaIsAutoInvoice,
											) ? (
												<MyCheckCircleIcon className="text-success" />
											) : (
												<MyCancelIcon className="text-danger" />
											)
										}
										label={
											safeBoolean(previewData?.tripolaPlan?.tripolaIsAutoInvoice)
												? "Enabled"
												: "Disabled"
										}
										sx={{ fontWeight: 600 }}
									/>
								</StatusField>
								<StatusField label="Auto Pay" span={4}>
									<Chip
										className={`tp-chip ${safeBoolean(previewData?.tripolaPlan?.tripolaIsAutoPay)
											? "text-success"
											: "text-danger"
											}`}
										icon={
											safeBoolean(
												previewData?.tripolaPlan?.tripolaIsAutoPay,
											) ? (
												<MyCheckCircleIcon className="text-success" />
											) : (
												<MyCancelIcon className="text-danger" />
											)
										}
										label={
											safeBoolean(previewData?.tripolaPlan?.tripolaIsAutoPay)
												? "Enabled"
												: "Disabled"
										}
										sx={{ fontWeight: 600 }}
									/>
								</StatusField>
							</div>
						</PreviewSection>
					</PreviewPanel>

					<PlanPanel title="Tripola Plan Information" icon={<MyCreditCardIcon />}>
						<PlanBlock title={`Plan Details${isPlanChanged ? " (upcoming)" : ""}`}>
							<div className="tp-plan-rows">
								<PlanRow
									label="Plan Name"
									icon={<MyCreditCardIcon fontSize="small" />}
									value={
										previewData?.tripolaPlan?.tripolaPlanId
											? displayValue(
													selectedTripolaPlan?.plan_name ||
														selectedTripolaPlan?.name
												)
											: "-"
									}
								/>
								<PlanRow
									label="Plan Price"
									icon={<MyAccountBalanceWalletIcon fontSize="small" />}
									value={
										previewData?.tripolaPlan?.tripolaPlanId
											? formatMoney(
													tripolaPlanPrice,
													currencyDetails?.symbol
												)
											: "-"
									}
									isPrice
								/>
							</div>
						</PlanBlock>

						<PlanBlock
							title={`Billing Information${isPlanChanged ? " (upcoming)" : ""}`}
						>
							<div className="tp-plan-rows">
								<PlanRow
									label="Billing Cycle"
									icon={<MyCalendarIcon fontSize="small" />}
									value={displayValue(tripolaBillingCycle)}
								/>
							</div>
						</PlanBlock>
					</PlanPanel>
				</div>
			)}
		</div>
	);
};

export default TenantCreationPreview;
